<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { searchApi } from '@/services/searchApi';
import type { SearchSuggestion } from '@/types/search';
import { courseDetailPath, kindLabel, tagVariant, tagLabel } from '@/utils/credentialTypes';
import { expandSuggestionsByAccessibleKinds } from '@/utils/searchSuggestions';
import { TypeTag } from '@/components/common';
import { useAccessibleLibraryStore } from '@/stores/accessibleLibraryStore';

const router = useRouter();
const route = useRoute();
const accessibleLibraryStore = useAccessibleLibraryStore();
const searchQuery = ref('');
const suggestions = ref<SearchSuggestion[]>([]);
const showDropdown = ref(false);
const loading = ref(false);
const focusedIndex = ref(-1);
const containerRef = ref<HTMLElement | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function getTypeLabel(kind: string): string {
  const code = kind.toLowerCase();
  // type-tag-ignore: only decides the label below; the tag itself is a TypeTag.
  const variant = tagVariant(code);
  if (code === 'microlearning') return 'Micro';
  if (code === 'skills') return 'Skills';
  // Format kinds must be named before the tagLabel() branch below. tagLabel()
  // resolves through the credential-type store, so a kind that earns no
  // credential isn't in it and comes back as the shouted code ("SIMULATION").
  // The branch is reached because the kind now HAS a colour — giving one a
  // palette variant without a label branch here regresses its label.
  if (code === 'simulation') return kindLabel(code);
  if (variant && variant !== '-color-neutral') return tagLabel(code);
  return kind;
}

// One dropdown row per variant the learner can access (a dual CLE+CPE course
// appears twice, each row linking to its variant URL). Kindless rows — SQL
// fallback, no accessible intersection, or the super-admin context — link
// bare. Super admins search the whole catalog, so no expansion there.
const displaySuggestions = computed(() =>
  isSuperAdmin()
    ? suggestions.value.map((s) => ({ ...s, key: s.id, linkKind: undefined }))
    : expandSuggestionsByAccessibleKinds(suggestions.value, accessibleLibraryStore.accessibleKinds));

async function fetchSuggestions(query: string) {
  if (query.length < 2) {
    suggestions.value = [];
    showDropdown.value = false;
    return;
  }

  loading.value = true;
  try {
    // The per-variant expansion needs the learner's accessible kinds; kick
    // the (cached) load alongside the fetch so rows render expanded.
    if (!isSuperAdmin()) {
      void accessibleLibraryStore.ensureLoaded();
    }

    suggestions.value = await searchApi.getSuggestions(query, 5);
    showDropdown.value = true;
    focusedIndex.value = -1;
  } catch {
    suggestions.value = [];
  } finally {
    loading.value = false;
  }
}

watch(searchQuery, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  if (!val.trim()) {
    suggestions.value = [];
    showDropdown.value = false;
    return;
  }
  debounceTimer = setTimeout(() => fetchSuggestions(val.trim()), 300);
});

const isSuperAdmin = () => route.path.startsWith('/super-admin');

function handleSearch() {
  if (searchQuery.value.trim()) {
    showDropdown.value = false;
    if (isSuperAdmin()) {
      router.push({ name: 'super-admin-courses', query: { q: searchQuery.value.trim() } });
    } else {
      router.push({ name: 'search', query: { q: searchQuery.value.trim() } });
    }
  }
}

function goToCourse(id: string, kind?: string) {
  showDropdown.value = false;
  searchQuery.value = '';
  if (isSuperAdmin()) {
    router.push({ name: 'super-admin-course-detail', params: { id } });
  } else {
    // Each dropdown row is a variant row — its kind lenses the course page;
    // kindless rows (no accessible variant) link bare.
    router.push(courseDetailPath(id, kind));
  }
}

function viewAllResults() {
  showDropdown.value = false;
  if (isSuperAdmin()) {
    router.push({ name: 'super-admin-courses', query: { q: searchQuery.value.trim() } });
  } else {
    router.push({ name: 'search', query: { q: searchQuery.value.trim() } });
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (!showDropdown.value) return;

  // Total items = expanded suggestion rows + "View all results" link
  const totalItems = displaySuggestions.value.length + 1;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      focusedIndex.value = focusedIndex.value < totalItems - 1 ? focusedIndex.value + 1 : 0;
      break;
    case 'ArrowUp':
      e.preventDefault();
      focusedIndex.value = focusedIndex.value > 0 ? focusedIndex.value - 1 : totalItems - 1;
      break;
    case 'Enter':
      e.preventDefault();
      if (focusedIndex.value >= 0 && focusedIndex.value < displaySuggestions.value.length) {
        goToCourse(displaySuggestions.value[focusedIndex.value]!.id, displaySuggestions.value[focusedIndex.value]!.linkKind);
      } else if (focusedIndex.value === displaySuggestions.value.length) {
        viewAllResults();
      } else {
        handleSearch();
      }
      break;
    case 'Escape':
      showDropdown.value = false;
      focusedIndex.value = -1;
      break;
  }
}

function handleClickOutside(e: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
    showDropdown.value = false;
    focusedIndex.value = -1;
  }
}

function handleFocus() {
  if (searchQuery.value.trim().length >= 2 && suggestions.value.length > 0) {
    showDropdown.value = true;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<template>
  <div class="search-bar-container" ref="containerRef">
    <div class="search-bar" id="header-search-container">
      <bb-icon name="search" decorative></bb-icon>
      <label for="header-search-input" class="sr-only">Search for courses</label>
      <input
        type="search"
        id="header-search-input"
        v-model="searchQuery"
        placeholder="Search for courses by keyword, jurisdiction, or title"
        autocomplete="off"
        role="combobox"
        :aria-expanded="showDropdown"
        aria-controls="search-suggestions-dropdown"
        aria-autocomplete="list"
        :aria-activedescendant="focusedIndex >= 0 ? `suggestion-${focusedIndex}` : undefined"
        @keydown="handleKeydown"
        @keyup.enter.exact="!showDropdown && handleSearch()"
        @focus="handleFocus"
      >
    </div>

    <div
      v-if="showDropdown"
      class="search-dropdown"
      id="search-suggestions-dropdown"
      role="listbox"
      aria-label="Search suggestions"
    >
      <div v-if="loading" class="search-dropdown-loading">
        Searching...
      </div>

      <template v-else-if="displaySuggestions.length > 0">
        <button
          v-for="(suggestion, index) in displaySuggestions"
          :key="suggestion.key"
          :id="`suggestion-${index}`"
          class="search-suggestion-item"
          role="option"
          :aria-selected="focusedIndex === index"
          :class="{ focused: focusedIndex === index }"
          @click="goToCourse(suggestion.id, suggestion.linkKind)"
          @mouseenter="focusedIndex = index"
        >
          <div class="suggestion-thumb">
            <img
              v-if="suggestion.thumbnailUrl"
              :src="suggestion.thumbnailUrl"
              :alt="suggestion.title"
            >
            <bb-icon v-else name="play_circle" decorative></bb-icon>
          </div>
          <span class="suggestion-title">{{ suggestion.title }}</span>
          <TypeTag v-if="suggestion.linkKind" :code="suggestion.linkKind">{{ getTypeLabel(suggestion.linkKind) }}</TypeTag>
        </button>

        <button
          :id="`suggestion-${displaySuggestions.length}`"
          class="search-suggestion-view-all"
          role="option"
          :aria-selected="focusedIndex === displaySuggestions.length"
          :class="{ focused: focusedIndex === displaySuggestions.length }"
          @click="viewAllResults"
          @mouseenter="focusedIndex = displaySuggestions.length"
        >
          View all results for "{{ searchQuery }}"
          <bb-icon name="arrow_forward" decorative></bb-icon>
        </button>
      </template>

      <div v-else class="search-dropdown-empty">
        No results found for "{{ searchQuery }}"
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-bar-container {
  position: relative;
  flex: 1;
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  z-index: 300;
  overflow: hidden;
}

.search-dropdown-loading,
.search-dropdown-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.search-suggestion-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-family-primary);
  font-size: 13px;
  color: var(--color-text-primary);
  transition: background 0.1s ease;
}

.search-suggestion-item:hover,
.search-suggestion-item.focused {
  background: var(--color-bg-hover);
}

.suggestion-thumb {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-default);
  overflow: hidden;
  flex-shrink: 0;
  background: var(--color-bg-page);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
}

.suggestion-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.suggestion-thumb bb-icon {
  font-size: 20px;
}

.suggestion-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-suggestion-view-all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-top: 1px solid var(--color-border-light);
  background: none;
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-link);
  transition: background 0.1s ease;
}

.search-suggestion-view-all:hover,
.search-suggestion-view-all.focused {
  background: var(--color-bg-hover);
  color: var(--color-link-hover);
}

.search-suggestion-view-all bb-icon {
  font-size: 16px;
}
</style>
